// Calendar reporting periods. Accounting amounts are supplied by existing ledger readers.
type AdvisorPeriod = { quarter: number | null; from: string; to: string; months: [number, number] }
const advisorReportPeriodApi = (() => {
  const resolve = (year: number, quarter?: unknown): AdvisorPeriod => {
    const q = quarter == null || quarter === '' ? null : Number(quarter);
    if (!Number.isInteger(year) || year < 1900 || year > 2200 || (q != null && (!Number.isInteger(q) || q < 1 || q > 4))) throw new Error('Invalid report period');
    const start = q == null ? 0 : (q - 1) * 3;
    const end = q == null ? 11 : start + 2;
    return { quarter: q, months: [start, end], from: `${year}-${String(start + 1).padStart(2, '0')}-01`,
      to: `${year}-${String(end + 1).padStart(2, '0')}-${new Date(Date.UTC(year, end + 1, 0)).getUTCDate()}` };
  };
  const sumMonths = (values: any[], period: AdvisorPeriod) => (values || []).slice(period.months[0], period.months[1] + 1).reduce((sum, value) => sum + (Number(value) || 0), 0);
  const budgetLines = (lines: any[], period: AdvisorPeriod) => (lines || []).map((line) => {
    const values = line.monthlyAmounts?.length ? line.monthlyAmounts : Array(12).fill((Number(line.annualAmount) || 0) / 12);
    const selected = values.slice(period.months[0], period.months[1] + 1);
    return { ...line, annualAmount: sumMonths(values, period), monthlyAmounts: selected };
  });

  const recentMonths = (now = new Date(), count = 24) => Array.from({ length: count }, (_, offset) => {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1, 12);
    const year = date.getFullYear();
    const monthIndex = date.getMonth();
    const month = monthIndex + 1;
    const id = `${year}-${String(month).padStart(2, '0')}`;
    return {
      kind: 'month', id, year, month, monthIndex, months: [monthIndex, monthIndex] as [number, number],
      from: `${id}-01`,
      to: `${id}-${new Date(Date.UTC(year, month, 0)).getUTCDate()}`,
    };
  });

  const resolveMonth = (value: unknown, now = new Date()) => {
    const id = String(value || '');
    const period = recentMonths(now).find((entry) => entry.id === id);
    if (!period) throw new Error('Invalid monthly report period');
    return period;
  };

  const trendMonths = (ctx: any, period: any) => [
    ...(ctx.previousChart || []).slice(period.monthIndex + 1).map((entry: any, index: number) => ({
      ...entry, year: period.year - 1, monthIndex: period.monthIndex + 1 + index,
    })),
    ...(ctx.chart || []).slice(0, period.monthIndex + 1).map((entry: any, monthIndex: number) => ({
      ...entry, year: period.year, monthIndex,
    })),
  ].slice(-12);

  const scopeMonth = (ctx: any, period: any, readers: any) => {
    const year = period.year;
    const selected = period.months;
    const previousMonthYear = period.monthIndex === 0 ? year - 1 : year;
    const previousMonthIndex = period.monthIndex === 0 ? 11 : period.monthIndex - 1;
    const report = readers.result(year, selected);
    const previousReport = readers.result(year - 1, selected);
    const previousMonthReport = readers.result(previousMonthYear, [previousMonthIndex, previousMonthIndex]);
    const budget = readers.budget(year);
    const plan = readers.plan({ year,
      budgetLines: budgetLines(budget?.budgetLines, period),
      actualLines: budgetLines(budget?.actualLines, period), periodMonths: 1 });
    return {
      ...ctx,
      year,
      period,
      monthsElapsed: 1,
      report,
      previousReport,
      previousMonthReport,
      previousYearAvailable: Boolean(previousReport.inntekter || previousReport.kostnader || previousReport.resultat),
      chart: (ctx.chart || []).slice(period.monthIndex, period.monthIndex + 1),
      chartMonthStart: period.monthIndex,
      monthlyTrend: trendMonths(ctx, period),
      rollingChart: ctx.chart,
      rollingMonths: period.monthIndex + 1,
      plan,
      trial: [],
      equityReconciliation: null,
    };
  };
  const scope = (ctx: any, period: AdvisorPeriod, readers: any) => {
    if (!period.quarter) return ctx;
    const year = ctx.year;
    const report = readers.result(year, period.months);
    const previousReport = readers.result(year - 1, period.months);
    const budget = readers.budget(year);
    const plan = readers.plan({ year, budgetLines: budgetLines(budget?.budgetLines, period),
      actualLines: budgetLines(budget?.actualLines, period), periodMonths: 3 });
    const previousCosts = new Map((previousReport.sections?.find((s: any) => s.key === 'kostnader')?.lines || []).map((l: any) => [String(l.nr), Number(l.amount) || 0]));
    const costs = (report.sections?.find((s: any) => s.key === 'kostnader')?.lines || []).map((line: any) => {
      const before = Number(previousCosts.get(String(line.nr))) || 0;
      const chg = before ? (Number(line.amount) - before) / Math.abs(before) * 100 : null;
      return { ...line, chg, flag: chg != null && chg > 15 };
    });
    // Historical open items cannot be reconstructed from today's remaining amounts.
    // Operational sections explicitly retain today's status; balance comes from the ledger cutoff.
    return { ...ctx, period, currentContext: ctx, monthsElapsed: 3, report, previousReport, plan,
      previousYearAvailable: Boolean(previousReport.inntekter || previousReport.kostnader || previousReport.resultat),
      trial: readers.trial(year, period.months), sales: readers.sales(year, period.months),
      advisor: { ...ctx.advisor, costs }, partyInsights: readers.parties(year, period.months),
      chart: ctx.chart.slice(period.months[0], period.months[1] + 1), chartMonthStart: period.months[0],
      rollingChart: ctx.chart, rollingMonths: period.months[1] + 1,
      home: { ...ctx.home, analysis: { ...ctx.home?.analysis, solidity: null } },
      equityReconciliation: null,
    };
  };
  return { resolve, resolveMonth, recentMonths, scope, scopeMonth, sumMonths, budgetLines, trendMonths };
})();
Object.assign(window, { PlummeAdvisorReportPeriod: advisorReportPeriodApi });
